import React, { useState, useEffect, useMemo } from 'react';
import AutorizacionModal from '../../../Auth/AutorizacionModal';
import API from '../../../../utils/api';
import './MoverProductosModal.css';

const MoverProductosModal = ({
  isOpen,
  onClose,
  onMoverProductos,
  productosDisponibles = [],
  mesaActual,
  tipoPedido = 'mesa'
}) => {
  // Estados para el modal
  const [productosSeleccionados, setProductosSeleccionados] = useState([]);
  const [mesaDestino, setMesaDestino] = useState(null);
  const [mesasDisponibles, setMesasDisponibles] = useState([]);
  const [loadingMesas, setLoadingMesas] = useState(false);
  const [mostrarAutorizacion, setMostrarAutorizacion] = useState(false);
  const [step, setStep] = useState(1); // 1: Seleccionar productos, 2: Seleccionar mesa

  // Resetear estado al abrir/cerrar modal
  useEffect(() => {
    if (isOpen) {
      setProductosSeleccionados([]);
      setMesaDestino(null);
      setStep(1);
      cargarMesasDisponibles();
    }
  }, [isOpen]);

  // Función para cargar mesas disponibles
  const cargarMesasDisponibles = async () => {
    if (tipoPedido !== 'mesa') return;

    setLoadingMesas(true);
    try {
      const data = await API.mesas.getActivas();
      // Filtrar mesas que no sean la actual
      const mesasFiltradas = data.filter(mesa =>
        mesa.idMesa !== mesaActual?.idMesa && mesa.statusMesa == 'ocupada'
      );
      setMesasDisponibles(mesasFiltradas);
    } catch (error) {
      console.error('Error al cargar mesas:', error);
    } finally {
      setLoadingMesas(false);
    }
  };

  // Función para manejar selección de productos
  const toggleProducto = (producto) => {
    setProductosSeleccionados(prev => {
      const yaSeleccionado = prev.find(p => p.idPedidoDetalle === producto.idPedidoDetalle);
      if (yaSeleccionado) {
        return prev.filter(p => p.idPedidoDetalle !== producto.idPedidoDetalle);
      } else {
        return [...prev, producto];
      }
    });
  };

  // Función para seleccionar todos los productos
  const toggleTodos = () => {
    if (productosSeleccionados.length === productosDisponibles.length) {
      setProductosSeleccionados([]);
    } else {
      setProductosSeleccionados([...productosDisponibles]);
    }
  };

  // Función para continuar al siguiente paso
  const continuarASiguientePaso = () => {
    if (productosSeleccionados.length === 0) {
      alert('Debe seleccionar al menos un producto');
      return;
    }
    setStep(2);
  };

  // Función para iniciar el proceso de autorización
  const iniciarMover = () => {
    if (!mesaDestino) {
      alert('Debe seleccionar una mesa de destino');
      return;
    }
    setMostrarAutorizacion(true);
  };

  // Función para manejar autorización exitosa
  const handleAutorizacionExitosa = async (authData) => {
    try {
      const resultado = await onMoverProductos({
        productos: productosSeleccionados,
        mesaDestino: mesaDestino,
        mesaOrigen: mesaActual,
        authData: authData
      });

      if (resultado.success) {
        setMostrarAutorizacion(false);
        onClose();
      }
    } catch (error) {
      console.error('Error al mover productos:', error);
    }
  };

  // Función para cancelar autorización
  const handleAutorizacionCancelada = () => {
    setMostrarAutorizacion(false);
  };

  // Calcular totales
  const totales = useMemo(() => {
    return productosSeleccionados.reduce((acc, producto) => {
      return {
        cantidad: acc.cantidad + producto.cantidad,
        subtotal: acc.subtotal + producto.subtotal
      };
    }, { cantidad: 0, subtotal: 0 });
  }, [productosSeleccionados]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-overlay">
        <div className="mover-productos-modal">
          <div className="modal-header">
            <h3>Mover Productos a Otra Mesa</h3>
            <button className="btn-cerrar-modal" onClick={onClose}>
              ×
            </button>
          </div>

          <div className="modal-body">
            {step === 1 && (
              <div className="paso-seleccionar-productos">
                <div className="paso-header">
                  <h4>Paso 1: Seleccionar Productos</h4>
                  <p>Seleccione los productos que desea mover de <strong>{mesaActual?.nombreMesa || 'esta mesa'}</strong></p>
                </div>

                <div className="controles-seleccion">
                  <label className="checkbox-control">
                    <input
                      type="checkbox"
                      checked={productosSeleccionados.length === productosDisponibles.length && productosDisponibles.length > 0}
                      onChange={toggleTodos}
                    />
                    Seleccionar todos ({productosDisponibles.length} productos)
                  </label>

                  {productosSeleccionados.length > 0 && (
                    <div className="seleccion-info">
                      <span className="badge-seleccion">
                        {productosSeleccionados.length} seleccionados - ${totales.subtotal.toFixed(2)}
                      </span>
                    </div>
                  )}
                </div>

                <div className="lista-productos">
                  {productosDisponibles.map(producto => (
                    <div key={producto.idPedidoDetalle} className="producto-item">
                      <label className="producto-checkbox">
                        <input
                          type="checkbox"
                          checked={productosSeleccionados.some(p => p.idPedidoDetalle === producto.idPedidoDetalle)}
                          onChange={() => toggleProducto(producto)}
                        />
                        <div className="producto-info">
                          <div className="producto-nombre">{producto.nombre}</div>
                          <div className="producto-detalles">
                            <span className="cantidad">Cant: {producto.cantidad}</span>
                            <span className="precio">${producto.precio.toFixed(2)}</span>
                            <span className="subtotal">${producto.subtotal.toFixed(2)}</span>
                          </div>
                          {producto.instrucciones && (
                            <div className="producto-instrucciones">
                              📝 {producto.instrucciones}
                            </div>
                          )}
                        </div>
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="paso-seleccionar-mesa">
                <div className="paso-header">
                  <h4>Paso 2: Seleccionar Mesa Destino</h4>
                  <p>
                    Mover <strong>{productosSeleccionados.length} producto(s)</strong> por
                    <strong> ${totales.subtotal.toFixed(2)}</strong>
                  </p>
                </div>

                <div className="productos-seleccionados-resumen">
                  <h5>Productos seleccionados:</h5>
                  <ul>
                    {productosSeleccionados.map(producto => (
                      <li key={producto.idPedidoDetalle}>
                        {producto.cantidad}x {producto.nombre} - ${producto.subtotal.toFixed(2)}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mesas-disponibles">
                  <h5>Seleccione mesa destino:</h5>
                  {loadingMesas ? (
                    <div className="loading">Cargando mesas...</div>
                  ) : mesasDisponibles.length === 0 ? (
                    <div className="no-mesas">No hay mesas activas disponibles</div>
                  ) : (
                    <div className="lista-mesas">
                      {mesasDisponibles.map(mesa => (
                        <label key={mesa.idMesa} className="mesa-option">
                          <input
                            type="radio"
                            name="mesaDestino"
                            value={mesa.idMesa}
                            checked={mesaDestino?.idMesa === mesa.idMesa}
                            onChange={() => setMesaDestino(mesa)}
                          />
                          <div className="mesa-info">
                            <div className="mesa-nombre">{`Mesa ${mesa.nombreMesa}` || `Mesa ${mesa.numero}`}</div>
                            <div className="mesa-detalles">
                              <span className="mesa-pedido">Pedido #{mesa.numeroPedido}</span>
                              <span>Mesero:</span><span className="mesa-mesero">{mesa.nombreMesero}</span>
                            </div>
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="modal-footer">
            {step === 1 && (
              <>
                <button className="btn-cancelar" onClick={onClose}>
                  Cancelar
                </button>
                <button
                  className="btn-continuar"
                  onClick={continuarASiguientePaso}
                  disabled={productosSeleccionados.length === 0}
                >
                  Continuar ({productosSeleccionados.length} productos)
                </button>
              </>
            )}

            {step === 2 && (
              <>
                <button className="btn-atras" onClick={() => setStep(1)}>
                  ← Atrás
                </button>
                <button className="btn-cancelar" onClick={onClose}>
                  Cancelar
                </button>
                <button
                  className="btn-mover"
                  onClick={iniciarMover}
                  disabled={!mesaDestino}
                >
                  Autorizar Movimiento
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      <AutorizacionModal
        isOpen={mostrarAutorizacion}
        onClose={handleAutorizacionCancelada}
        onAuthorize={handleAutorizacionExitosa}
        accion={`mover ${productosSeleccionados.length} producto(s) de ${mesaActual?.nombreMesa} a ${mesaDestino?.nombreMesa || mesaDestino?.nombre}`}
        perfilRequerido="moverProductos"
        titulo="Autorización para Mover Productos"
        descripcion={`Se requiere autorización para mover productos entre mesas. Total: $${totales.subtotal.toFixed(2)}`}
        solicitarRazon={true}
        labelRazon="Motivo del traslado"
      />
    </>
  );
};

export default MoverProductosModal;